import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Droplets, Flame, Gauge, CheckCircle, ArrowRight, Phone, ShieldCheck, Home, MapPin, Clock, Award } from 'lucide-react';

const PHONE = "403-615-8770";

const servicesData = {
  plumbing: {
    name: 'Plumbing',
    icon: Droplets,
    color: '#0EA5E9',
    hero: 'Professional Plumbing Services in Calgary',
    metaTitle: 'Calgary Plumber | Expert Plumbing Services | DNB',
    metaDescription: 'Expert plumbers in Calgary. Poly-B replacement, tankless water heaters, drain cleaning, leak repairs & more. Residential & light commercial. Call 403-615-8770.',
    description: 'DNB provides comprehensive plumbing services throughout Calgary and surrounding areas. Owner Mike Radinovic, a journeyman plumber &amp; gas fitter with 25+ years of experience, ensures quality workmanship you can trust. We serve residential homeowners, property managers, and light commercial clients with a full range of plumbing solutions.',
    longDescription: `Whether you're dealing with a plumbing emergency like a burst pipe or planning a bathroom renovation, our team is equipped to handle projects of all sizes. We use quality materials and proven techniques to deliver lasting results. As a locally owned Calgary business, we understand the unique plumbing challenges that come with our climate, from frozen pipes in winter to aging Poly-B plumbing systems found in many Calgary homes.
    
Our commitment to upfront pricing means no surprises on your bill. We'll assess your situation, explain your options, and provide a clear quote before any work begins. From simple repairs to complete plumbing installations for new homes and renovations, DNB is your trusted partner for all things plumbing in Calgary.`,
    benefits: [
      'Journeyman certified plumbers',
      'Upfront, transparent pricing',
      'Quality materials guaranteed',
      'Clean, professional service',
      'Same-day emergency response',
      'Serving Calgary & surrounding areas'
    ],
    services: [
      { name: 'Poly-B Replacement', description: 'Many Calgary homes built between 1978 and 1995 contain Poly-B (polybutylene) plumbing, which can deteriorate over time and lead to costly leaks and water damage. Our Poly-B replacement service upgrades your home to modern, reliable PEX plumbing. We handle everything from small sections to complete whole-home repiping, minimizing disruption while maximizing your home\'s plumbing reliability and value.' },
      { name: 'Tankless Water Heater Installation', description: 'Upgrade to endless hot water with a tankless water heater. These energy-efficient units heat water on demand, saving space and reducing energy costs. Our certified technicians properly size and install tankless systems from leading manufacturers, ensuring optimal performance for your household\'s hot water needs.' },
      { name: 'Tankless Water Heater Service', description: 'Keep your tankless water heater running at peak efficiency with regular service. We perform descaling, filter cleaning, and system checks to prevent mineral buildup and extend the life of your unit. If repairs are needed, we diagnose and fix issues quickly to restore your hot water supply.' },
      { name: 'Hot Water Tank Replacement', description: 'When your hot water tank fails or reaches the end of its lifespan (typically 8-12 years), we provide professional replacement services. We help you choose the right size and efficiency rating for your household, handle proper disposal of your old tank, and ensure your new water heater is installed to code.' },
      { name: 'Hot Water Tank Repairs', description: 'No hot water? Our technicians quickly diagnose and repair hot water tank issues including faulty thermostats, heating elements, pilot lights, and pressure relief valves. We service all major brands and can often restore your hot water the same day.' },
      { name: 'Drain Cleaning', description: 'Slow or blocked drains are more than an inconvenience—they can lead to bigger problems if left untreated. We use professional-grade equipment to clear blockages in kitchen sinks, bathroom drains, and main sewer lines. Our thorough approach addresses the root cause, not just the symptom.' },
      { name: 'Leak Repairs', description: 'Water leaks can cause significant damage to your home if not addressed promptly. Our team responds quickly to locate and repair leaks in pipes, fixtures, and connections. We also offer leak detection services to identify hidden leaks before they become major problems.' },
      { name: 'Bathroom Plumbing', description: 'Complete bathroom plumbing services including toilet installation and repair, sink and faucet installation, shower and bathtub plumbing, and rough-in work for bathroom renovations. We ensure proper drainage, water supply, and venting for reliable bathroom performance.' },
      { name: 'Kitchen Plumbing', description: 'Professional kitchen plumbing services including sink installation, faucet replacement, dishwasher connections, garbage disposal installation, and under-sink plumbing repairs. We handle both minor repairs and complete kitchen plumbing for renovations.' },
      { name: 'Renovation Plumbing', description: 'Planning a renovation? Our plumbers work seamlessly with your project timeline and coordinate with other trades. From rough-in to finish, we handle plumbing relocations, fixture installations, and system upgrades to bring your renovation vision to life.' },
      { name: 'New Home Plumbing', description: 'Complete plumbing systems for new construction projects. We work from blueprints to install code-compliant plumbing infrastructure including water supply lines, drain-waste-vent systems, and all fixtures. Quality installation from the start means fewer problems down the road.' }
    ]
  },
  heating: {
    name: 'Heating',
    icon: Flame,
    color: '#EF4444',
    hero: 'Expert Heating Services in Calgary',
    metaTitle: 'Calgary Heating Services | Furnace & Boiler Experts | DNB',
    metaDescription: 'Expert heating services in Calgary. Furnace installation & repair, in-floor radiant heating, boiler & hydronic systems. Certified technicians. Call 403-615-8770.',
    description: 'Calgary winters demand reliable heating systems. DNB provides expert heating services including furnace installation and repair, in-floor radiant heating, and boiler/hydronic system service. Our certified technicians ensure your home stays warm and comfortable when you need it most.',
    longDescription: `With over 25 years of experience in the trades, owner Mike Radinovic and the DNB team have worked on heating systems in residential and light commercial settings. This experience means we bring a deep understanding of heating system design and repair to every Calgary home we service.
    
We specialize in high-efficiency furnaces that reduce your energy bills while keeping your home comfortable. Our in-floor radiant heating installations provide luxurious, even warmth throughout your home. For homes with hydronic heating systems—common in Calgary townhouses and older homes—we offer comprehensive service including circulator pump replacement, zone valve repairs, and system purging.

Emergency heating repairs are available because we know that a furnace breakdown in a Calgary winter is more than an inconvenience—it's an emergency. Contact us for urgent heating issues.`,
    benefits: [
      'Certified heating technicians',
      'Emergency repair service available',
      'Energy-efficient system options',
      'Comprehensive warranties',
      'All furnace makes and models',
      'Hydronic heating specialists'
    ],
    services: [
      { name: 'Furnace Installation', description: 'Professional installation of high-efficiency furnaces from leading manufacturers. We properly size your new furnace for your home, ensure correct ductwork connections, and optimize settings for maximum efficiency and comfort. Our installations include proper permits and inspections to meet all Calgary building codes.' },
      { name: 'Furnace Repair', description: 'Fast, reliable furnace repairs when you need them most. We diagnose and fix issues with all major furnace brands including ignition problems, blower motor failures, heat exchanger issues, and thermostat malfunctions. Our goal is to restore your heat as quickly as possible.' },
      { name: 'In-Floor Radiant Heating', description: 'Experience the comfort of hydronic radiant floor heating. These systems circulate warm water through tubing beneath your floors, providing efficient, even heating without cold spots or drafts. Ideal for bathrooms, kitchens, basements, and whole-home installations. We design and install complete in-floor heating systems tailored to your home.' },
      { name: 'Boiler & Hydronic Heating', description: 'Comprehensive service for hydronic heating systems common throughout Calgary. We install, repair, and maintain boilers, circulator pumps, expansion tanks, zone valves, pressure controls, and system components. Our technicians are experienced in system purging, balancing, and troubleshooting hydronic heating problems.' },
      { name: 'Garage Heaters', description: 'Keep your garage warm for working on projects, hobbies, or protecting your vehicles from extreme cold. We install natural gas and propane garage heaters with proper ventilation, gas line connections, and safety controls. A warm garage extends the life of your vehicles and makes winter tasks more comfortable.' },
      { name: 'Thermostats', description: 'Upgrade to a smart or programmable thermostat for better comfort and energy savings. We install and configure thermostats from Nest, Ecobee, Honeywell, and other brands, ensuring proper connection to your heating system and helping you understand how to use your new thermostat effectively.' }
    ]
  },
  'gas-fitting': {
    name: 'Gas Fitting',
    icon: Gauge,
    color: '#F59E0B',
    hero: 'Licensed Gas Fitting Services in Calgary',
    metaTitle: 'Gas Fitting Calgary | BBQ Lines & Gas Appliances | DNB',
    metaDescription: 'Licensed gas fitting services in Calgary. BBQ gas lines, garage heater hookups, gas appliance installation. Safe, code-compliant work. Call 403-615-8770.',
    description: 'DNB provides licensed gas fitting services throughout Calgary. Our certified gas fitters handle BBQ gas lines, garage heater connections, gas appliance installations, and more. Every job is completed safely and to code.',
    longDescription: `Gas work requires proper training, certification, and attention to safety. As a licensed gas fitting contractor, DNB ensures all gas installations meet Alberta safety codes and manufacturer requirements. Our gas fitters hold professional certification, demonstrating the highest level of competency in the trade.
    
Natural gas adds convenience to your home—from cooking with a gas range to enjoying your outdoor BBQ without propane tanks. We install gas lines for a variety of applications including BBQs, outdoor kitchens, garage heaters, fire pits, pool heaters, and backup generators.

All gas work requires proper permits and inspections. We handle the permit process and ensure your installation passes inspection the first time. Safety is our top priority on every gas fitting project.`,
    benefits: [
      'Certified gas fitters',
      'Code-compliant installations',
      'Proper permits included',
      'Safety testing on all work',
      'All gas appliances',
      'Emergency gas service'
    ],
    services: [
      { name: 'Gas Line Installation', description: 'Professional gas line installation for new appliances and equipment. We properly size gas lines for adequate flow, use appropriate materials, and ensure all connections are secure and leak-free. Every installation includes pressure testing and leak detection.' },
      { name: 'BBQ Gas Lines', description: 'Enjoy convenient natural gas for your outdoor BBQ. No more propane tank refills or running out of fuel mid-cookout. We install underground or above-ground gas lines to your BBQ location with a quick-connect fitting for easy seasonal hookup and disconnection.' },
      { name: 'Garage Heater Gas Lines', description: 'Gas line installation for garage heaters. We size the line appropriately for your heater\'s BTU requirements and ensure proper routing. Combined with our heating services, we can provide complete garage heater installation including the gas line, heater, and thermostat.' },
      { name: 'Equipment & Gas Appliance Installations', description: 'Professional installation of gas ranges, dryers, fireplaces, fire pits, pool heaters, generators, and other gas-powered equipment. We ensure proper gas supply, venting (where required), and safe operation of all gas appliances.' },
      { name: 'Gas Fireplace Connections', description: 'Add warmth and ambiance with a gas fireplace or fire pit. We install gas lines for indoor fireplaces, outdoor fire pits, and patio heaters. Gas fireplaces provide instant heat and flame with none of the mess of wood burning.' }
    ]
  },
  'backflow-testing': {
    name: 'Backflow Testing',
    icon: ShieldCheck,
    color: '#10B981',
    hero: 'City of Calgary Backflow Preventer Testing',
    metaTitle: 'Backflow Testing Calgary | Cross Connection Control | DNB',
    metaDescription: 'City of Calgary certified backflow preventer testing. Annual cross connection control testing for compliance. Protect your water supply. Call 403-615-8770.',
    description: 'Cross Connection Control Testing ensures backflow preventers are operating correctly and protecting the drinking water supply. Most municipalities, including the City of Calgary, require annual testing of backflow preventers to prevent contaminated water from flowing back into the clean water system.',
    longDescription: `Backflow preventers are critical safety devices that protect Calgary's drinking water supply from contamination. These devices are required on irrigation systems, fire suppression systems, boiler systems, and any connection that could allow non-potable water to flow backwards into the city water supply.

The City of Calgary requires annual testing of backflow preventers by certified testers. Failure to test can result in fines and potential disconnection of your water service. Our certified technicians perform testing efficiently, provide required documentation to the City, and can repair or replace faulty devices.

Annual testing helps protect public health by ensuring these critical safety devices are working properly. If a backflow preventer fails testing, we can often repair it on the spot or provide a quote for replacement if needed.`,
    benefits: [
      'City of Calgary certified testers',
      'Annual testing compliance',
      'Documentation submitted to City',
      'Same-day testing available',
      'Repair services if needed',
      'Commercial & residential'
    ],
    services: [
      { name: 'Annual Backflow Testing', description: 'Annual testing of backflow preventers as required by the City of Calgary. We test all types of backflow devices including pressure vacuum breakers, double check valves, and reduced pressure zone devices. Test results are documented and submitted to the City on your behalf.' },
      { name: 'Cross Connection Control Testing', description: 'Comprehensive testing of cross connection control devices. We verify proper operation of all components including check valves, relief valves, and test cocks. Testing includes differential pressure readings and visual inspection for damage or wear.' },
      { name: 'City of Calgary Compliance', description: 'We help you maintain compliance with City of Calgary backflow testing requirements. This includes tracking your testing schedule, providing advance notification when testing is due, and ensuring all documentation is properly submitted to city records.' },
      { name: 'Backflow Preventer Installation', description: 'Installation of new backflow preventers for irrigation systems, boiler makeup water, and other applications requiring cross connection control. We help you select the appropriate type of device for your application and ensure proper installation to code.' }
    ]
  },
  'secondary-suites': {
    name: 'Secondary Suites',
    icon: Home,
    color: '#8B5CF6',
    hero: 'Legal Secondary Suite Plumbing in Calgary',
    metaTitle: 'Secondary Suite Plumbing Calgary | Basement Suite | DNB',
    metaDescription: 'Legal secondary suite plumbing in Calgary. Basement suite drainage, water supply, fixtures. Code-compliant installations for permit approval. Call 403-615-8770.',
    description: 'We assist homeowners with plumbing installations required for legal basement suites, including drainage systems, water supply lines, fixture installations, and code-compliant plumbing. Get your secondary suite properly plumbed to meet City of Calgary requirements.',
    longDescription: `Adding a legal secondary suite to your Calgary home can provide rental income and increase property value. However, proper plumbing is essential for both permit approval and tenant safety. DNB specializes in secondary suite plumbing that meets City of Calgary building code requirements.

Secondary suite plumbing typically includes a kitchen with sink, a bathroom with toilet, sink, and shower/tub, and often laundry facilities. Each of these requires proper drainage connections, water supply lines, and venting. The complexity depends on your home's existing plumbing layout and the suite's location relative to the main drain.

We work with homeowners and contractors on secondary suite projects, providing plumbing design input during the planning phase and quality installation during construction. Our experience with Calgary building code requirements helps ensure your project passes inspection.`,
    benefits: [
      'Calgary building code expertise',
      'Permit-ready installations',
      'Coordination with other trades',
      'Design assistance available',
      'Quality materials & workmanship',
      'Inspection support'
    ],
    services: [
      { name: 'Basement Suite Plumbing', description: 'Complete plumbing installations for basement secondary suites. We handle kitchen plumbing, bathroom installations, and laundry hookups. Our work is designed and installed to meet City of Calgary requirements for legal secondary suites.' },
      { name: 'Drainage Systems', description: 'Proper drainage is critical for secondary suite plumbing. We install drain lines for all fixtures, ensure proper venting, and connect to your home\'s main drain system. For basement suites below the main sewer line, we can install sewage ejector systems.' },
      { name: 'Water Supply Lines', description: 'Installation of hot and cold water supply lines to serve secondary suite fixtures. We ensure adequate water pressure and flow, proper pipe sizing, and accessible shut-off valves for the suite\'s water supply.' },
      { name: 'Code-Compliant Installations', description: 'All our secondary suite plumbing work meets City of Calgary building code requirements. We provide documentation as needed for permit applications and work with city inspectors to ensure approval of your secondary suite project.' }
    ]
  }
};

export const ServiceDetailPage = () => {
  const { serviceType } = useParams();
  const service = servicesData[serviceType];

  if (!service) {
    return (
      <main className="min-h-screen flex items-center justify-center section-tint">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-[#0F172A] mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#0F172A] hover:underline">
            View All Services
          </Link>
        </div>
      </main>
    );
  }

  const Icon = service.icon;

  return (
    <main data-testid={`service-detail-${serviceType}`} className="page-transition">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{service.metaTitle}</title>
        <meta name="description" content={service.metaDescription} />
        <meta property="og:title" content={service.metaTitle} />
        <meta property="og:description" content={service.metaDescription} />
        <link rel="canonical" href={`https://dnbplumbing.ca/services/${serviceType}`} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 section-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div 
                className="icon-container"
                style={{ background: `linear-gradient(135deg, ${service.color}15, ${service.color}05)` }}
              >
                <Icon className="w-8 h-8" style={{ color: service.color }} strokeWidth={1.5} />
              </div>
              <span className="text-[#0F172A] uppercase tracking-widest text-sm font-semibold">{service.name}</span>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] mb-6">
              {service.hero}
            </h1>
            <p className="text-slate-600 max-w-3xl text-lg mb-4">
              {service.description}
            </p>
            <p className="text-[#0F172A] font-semibold mb-8">
              Residential • Light Commercial • Service & Renovation
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="service-detail-book-btn"
                className="btn-primary rounded-lg inline-flex items-center justify-center gap-2"
              >
                Book Service
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href={`tel:${PHONE}`}
                className="btn-secondary rounded-lg inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call {PHONE}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {service.benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm border border-slate-200"
              >
                <CheckCircle className="w-5 h-5 text-[#0F172A] flex-shrink-0" />
                <span className="text-slate-700 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Long Description */}
      {service.longDescription && (
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              {service.longDescription.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-slate-600 mb-4">{paragraph.trim()}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services List */}
      <section className="py-16 section-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="font-heading font-bold text-3xl text-[#0F172A] mb-4">
              Our {service.name} Services
            </h2>
            <p className="text-slate-600 max-w-2xl">
              Comprehensive {service.name.toLowerCase()} solutions for Calgary homeowners and businesses.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {service.services.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                data-testid={`service-item-${index}`}
                className="group p-6 bg-white rounded-xl shadow-sm border border-slate-200 hover:border-[#0F172A]/30 hover:shadow-md transition-all"
              >
                <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-3 group-hover:text-[#0F172A] transition-colors">
                  {item.name}
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {item.description}
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 text-[#0F172A] text-sm font-medium hover:gap-3 transition-all"
                >
                  Request Service
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-3xl text-[#0F172A] mb-6">
                Why Choose DNB for {service.name}?
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#0F172A]/10 rounded-lg">
                    <Award className="w-6 h-6 text-[#0F172A]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0F172A]">Expert Technicians</h4>
                    <p className="text-slate-600 text-sm">Our journeyman holds multiple trade certificates in plumbing, gas fitting, and pipefitting.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#0F172A]/10 rounded-lg">
                    <Clock className="w-6 h-6 text-[#0F172A]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0F172A]">Fast Response</h4>
                    <p className="text-slate-600 text-sm">Same-day service available for many {service.name.toLowerCase()} issues in Calgary.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-[#0F172A]/10 rounded-lg">
                    <MapPin className="w-6 h-6 text-[#0F172A]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#0F172A]">Local Experts</h4>
                    <p className="text-slate-600 text-sm">Calgary-based team serving the community since 2020.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="section-tint rounded-2xl p-8 border border-slate-200">
              <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-4">
                Get a Quote for {service.name}
              </h3>
              <p className="text-slate-600 mb-6">
                Contact us today for fast, reliable {service.name.toLowerCase()} service in Calgary and surrounding areas.
              </p>
              <div className="space-y-3">
                <a
                  href={`tel:${PHONE}`}
                  className="w-full btn-primary rounded-lg flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Call {PHONE}
                </a>
                <Link
                  to="/contact"
                  className="w-full btn-secondary rounded-lg flex items-center justify-center gap-2"
                >
                  Request Online Quote
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-12 section-tint">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-4">{service.name} Service Areas</h3>
          <p className="text-slate-600 mb-6">We proudly serve Calgary and surrounding communities</p>
          <div className="flex flex-wrap justify-center gap-3">
            {['Calgary', 'Airdrie', 'Chestermere', 'Cochrane', 'Okotoks', 'High River', 'Rocky View County'].map((area) => (
              <span
                key={area}
                className="px-4 py-2 bg-white text-slate-700 text-sm rounded-lg font-medium border border-slate-200"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 cta-banner">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            Need {service.name} Service in Calgary?
          </h2>
          <p className="text-white/90 mb-6">
            Our experts are ready to help. Book now or call for fast, professional service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#0F172A] font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Service
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`tel:${PHONE}`}
              className="border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call Now
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetailPage;
